import type { ItemFieldValue } from '@/lib/api/item-store/field-values';
import type { CustomFieldTypes } from '@/lib/api/types/customField';
import type {
  Item,
  ItemCustomFieldValue,
  ItemCustomFieldValues,
  ItemLinkValue,
  ItemMultiLinkValue,
  ItemV2,
} from '@/lib/api/types/items';

const MULTI_FIELD_TYPES = new Set([
  'MULTI_PICKLIST',
  'MULTI_ITEM',
  'MULTI_USER',
  'MULTI_TEXT',
  'MULTI_LINK',
]);

export function reconcileMultiValues(
  fieldType: string,
  fieldValue: ItemFieldValue['fieldValue'],
): unknown[] {
  if (fieldType === 'MULTI_LINK') {
    return fieldValue.map(({ id, value, description }): ItemMultiLinkValue => {
      const entry: ItemMultiLinkValue = { id, MULTI_LINK: value as string };
      if (description !== undefined) {
        entry.description = description;
      }
      return entry;
    });
  }
  return fieldValue.map(({ id, value }) => ({ id, [fieldType]: value }));
}

export function reconcileFieldValue(
  fieldType: string,
  fieldValue: ItemFieldValue['fieldValue'],
): ItemCustomFieldValue | undefined {
  if (MULTI_FIELD_TYPES.has(fieldType)) {
    return reconcileMultiValues(fieldType, fieldValue) as ItemCustomFieldValue;
  }
  const first = fieldValue[0];
  if (!first) return undefined;
  if (fieldType === 'LINK') {
    const linkValue: ItemLinkValue = { LINK: first.value as string };
    if (first.description !== undefined) linkValue.description = first.description;
    return linkValue;
  }
  return { [fieldType]: first.value } as ItemCustomFieldValue;
}

export function customFieldV2ToV1(
  rawValue: unknown,
  fieldType: CustomFieldTypes,
): ItemCustomFieldValue | undefined {
  if (rawValue === null || rawValue === undefined) return undefined;

  if (
    typeof rawValue === 'object' &&
    !Array.isArray(rawValue) &&
    fieldType in (rawValue as object)
  ) {
    return rawValue as ItemCustomFieldValue;
  }

  switch (fieldType) {
    case 'CHECKBOX':
      return { CHECKBOX: rawValue as boolean };
    case 'TEXT':
      return { TEXT: rawValue as string };
    case 'MULTILINE_TEXT':
      return { MULTILINE_TEXT: rawValue as string };
    case 'NOTE':
      return { NOTE: rawValue as string };
    case 'NUMERIC':
      return { NUMERIC: rawValue as number };
    case 'CURRENCY':
      return { CURRENCY: rawValue as number };
    case 'PICKLIST':
      return { PICKLIST: rawValue as number };
    case 'ITEM':
      return { ITEM: rawValue as number };
    case 'USER':
      return { USER: rawValue as string };
    case 'DATE': {
      if (typeof rawValue === 'string') return { DATE: rawValue };
      if (typeof rawValue === 'object') {
        const d = rawValue as { year?: number; month?: number; day?: number };
        if (d.year === undefined || d.month === undefined || d.day === undefined) return undefined;
        const month = String(d.month).padStart(2, '0');
        const day = String(d.day).padStart(2, '0');
        return { DATE: `${d.year}-${month}-${day}` };
      }
      return undefined;
    }
    case 'LINK': {
      if (typeof rawValue === 'object' && 'value' in (rawValue as object)) {
        const linkObj = rawValue as { value: string; description?: string };
        return { LINK: linkObj.value, description: linkObj.description };
      }
      return { LINK: rawValue as string };
    }
    case 'MULTI_PICKLIST':
    case 'MULTI_ITEM':
    case 'MULTI_USER':
    case 'MULTI_TEXT':
    case 'MULTI_LINK':
      if (Array.isArray(rawValue)) {
        return rawValue.map((entry, index) => {
          if (typeof entry !== 'object' || entry === null) {
            return { id: -(Date.now() + index), [fieldType]: entry };
          }
          const obj = entry as Record<string, unknown>;
          if (fieldType in obj) return obj;
          if (fieldType === 'MULTI_LINK' && 'url' in obj) {
            return {
              id: obj.id ?? -(Date.now() + index),
              MULTI_LINK: obj.url,
              description: obj.description,
            };
          }
          return { id: obj.id ?? -(Date.now() + index), [fieldType]: obj.value ?? obj[fieldType] };
        }) as ItemCustomFieldValue;
      }
      return undefined;
    default:
      return undefined;
  }
}

type V2FieldValue = ItemV2['customFieldValues'][string];

export function customFieldV1ToV2(v1Value: ItemCustomFieldValue): V2FieldValue {
  if (Array.isArray(v1Value)) {
    return (v1Value as Array<Record<string, unknown>>).map((entry) => {
      const valueKey = Object.keys(entry).find((k) => k !== 'id' && k !== 'description');
      return (valueKey !== undefined ? entry[valueKey] : null) as string | number | boolean;
    });
  }
  const obj = v1Value as Record<string, unknown>;
  const valueKey = Object.keys(obj).find((k) => k !== 'description');
  return (valueKey !== undefined ? obj[valueKey] : null) as string | number | boolean | null;
}

export function itemV1ToV2(item: Item): ItemV2 {
  const { customFields, ...rest } = item;
  const customFieldValues: ItemV2['customFieldValues'] = {};
  if (customFields) {
    for (const [key, value] of Object.entries(customFields)) {
      customFieldValues[key] = customFieldV1ToV2(value);
    }
  }
  return { ...rest, customFieldValues };
}

export function itemV2ToV1(
  item: ItemV2,
  fieldDefs: ReadonlyArray<{ id: number; type: CustomFieldTypes }>,
): Item {
  const { customFieldValues, ...rest } = item;
  const typeById = new Map(fieldDefs.map((f) => [String(f.id), f.type]));
  const customFields: ItemCustomFieldValues = {};
  for (const [key, rawValue] of Object.entries(customFieldValues)) {
    if (rawValue === null || rawValue === undefined) continue;
    const fieldType = typeById.get(key);
    if (!fieldType) continue;
    const wrapped = customFieldV2ToV1(rawValue, fieldType);
    if (wrapped !== undefined) {
      customFields[key] = wrapped;
    }
  }
  return { ...rest, customFields };
}
